import React from 'react';
import { Button, Collapse, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { IUploadStepProps } from './i-import-wizard';

const { Dragger } = Upload;
const { Panel } = Collapse;

const MAX_VISIBLE_PARSE_ERRORS = 5;

const TEMPLATE_HEADER = 'code,description,Color,Size\n';

const CSV_EXAMPLE = [
  '# comments and blank lines are ignored',
  'code,description,Color,Size',
  'AT-1001,Blue crate,Blue,Large',
  'AT-1002,Red pallet,Red,Small',
].join('\n');

const JSON_EXAMPLE = [
  '[',
  '  { "code": "AT-1001", "description": "Blue crate", "Color": "Blue" },',
  '  { "code": "AT-1002", "description": "Red pallet", "Color": "Red" }',
  ']',
].join('\n');

const errorTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#E13333',
};

const exampleStyle: React.CSSProperties = {
  margin: '6px 0 0',
  padding: '10px 12px',
  background: '#F8F8FA',
  border: '1px solid #E1E3E8',
  borderRadius: 8,
  fontSize: 12,
  color: '#535559',
  whiteSpace: 'pre',
  overflowX: 'auto',
};

const hintStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: '#8C909B',
};

const downloadTemplate = (): void => {
  const blob = new Blob([TEMPLATE_HEADER], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = 'asset-types-template.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const UploadStep = (props: IUploadStepProps): JSX.Element => {
  const {
    fileName,
    fileError,
    parseErrors,
    busy,
    onFileSelected,
  } = props;

  const visibleErrors = parseErrors.slice(0, MAX_VISIBLE_PARSE_ERRORS);
  const hiddenErrorCount = parseErrors.length - visibleErrors.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Dragger
        accept=".csv,.tsv,.txt,.json"
        multiple={false}
        disabled={busy}
        showUploadList={false}
        beforeUpload={(file) => {
          onFileSelected(file);
          return false;
        }}
      >
        <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
          <InboxOutlined style={{ color: '#3800EE' }} />
        </p>
        <p style={{ margin: 0, fontSize: 14, color: '#190952' }}>
          Drag a file here, or click to choose one
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#8C909B' }}>
          CSV, TSV or JSON
        </p>
      </Dragger>

      {fileName ? (
        <div style={{ fontSize: 13, color: '#535559' }}>
          Selected file:
          {' '}
          <span style={{ color: '#190952', fontWeight: 500 }}>{fileName}</span>
        </div>
      ) : null}

      {fileError ? <div style={errorTextStyle}>{fileError}</div> : null}

      {visibleErrors.length ? (
        <div style={{
          ...errorTextStyle, display: 'flex', flexDirection: 'column', gap: 2,
        }}
        >
          {visibleErrors.map((error) => (
            <span key={`${error.line}-${error.message}`}>
              {`Line ${error.line}: ${error.message}`}
            </span>
          ))}
          {hiddenErrorCount > 0 ? <span>{`…and ${hiddenErrorCount} more`}</span> : null}
        </div>
      ) : null}

      <Collapse ghost>
        <Panel header="Expected format" key="format">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={hintStyle}>
              CSV, TSV or JSON. Lines starting with
              {' '}
              <code>#</code>
              {' '}
              and blank lines are ignored. The first line left is the header.
              Columns are matched by name and can be corrected in the next step.
            </p>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#535559' }}>CSV</div>
              <pre style={exampleStyle}>{CSV_EXAMPLE}</pre>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#535559' }}>JSON</div>
              <pre style={exampleStyle}>{JSON_EXAMPLE}</pre>
            </div>
          </div>
        </Panel>
      </Collapse>

      <div>
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={downloadTemplate}>
          Download CSV template
        </Button>
      </div>
    </div>
  );
};

UploadStep.displayName = 'UploadStep';

export default UploadStep;
